import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReduxState } from '../../store/types';
import { SipCall } from '../../store/actions/ringgroups/payloads';
import { useHistory, useLocation } from 'react-router';
import { actions } from '../../store';

type WebSocketObject = {
    cseq: number | undefined,
    action: string | undefined,
    event: string | undefined,
    result: {
        success: boolean | undefined,
        call_info: SipCall | undefined
    },
    success: boolean | undefined,
};

type WebSocketDataProviderProps = {
    webSocketURL: string,
    callStatusUpdate: (call: SipCall) => void,
    recalcCallsDurationEvent?: () => void
};

const WebSocketDataProvider: React.FC<WebSocketDataProviderProps> = ({
    webSocketURL,
    children,
    callStatusUpdate,
    recalcCallsDurationEvent
}) => {
    const dispatch = useDispatch();
    const prevLocation = useLocation().pathname;
    const history = useHistory();
    const { session_id, csrf_token } = useSelector((state: ReduxState) => state.auth);
    const i_customer = useSelector(
        (state: ReduxState) => state.generic.sessionData?.i_customer,
    );
    
    const updateStatus = useCallback((object) => {
        if(object.event === 'sip.call_control_notifications' && object.result.call_info)
        {
            const sipCall = object.result.call_info as SipCall;
            if(sipCall?.is_external_call === true)
            {
                //ICAP-2478: If an event has a flag is_external_call, such events must be ignored
            }
            else
            {
                callStatusUpdate(sipCall);
            }
        }
        else if(object.event === 'sip.call_control_notifications' && object.result.participant_info?.event === 'ParticipantInfo')
        {
            dispatch(actions.getConferenceParticipantsList.request({
                name: object.result.participant_info.conference_name,
                owner_id: object.result.participant_info.conference_owner_id,
            }));
        }
    }, [callStatusUpdate]);
    
    useEffect(() => {
        let shouldInitSignaling = false;
        let shouldDestroySignaling = false;

        if(!session_id || !callStatusUpdate) {
            shouldDestroySignaling = true;
        }
        
        //@ts-ignore
        if(!window.signaling && session_id && callStatusUpdate) {
            shouldInitSignaling = true;
        }

        //@ts-ignore
        if(shouldDestroySignaling && window.signaling) {
            try{
                //@ts-ignore
                window.signaling?.close();
            } catch {

            }
            //@ts-ignore
            window.signaling = null;
        }

        if(shouldInitSignaling) {
            //@ts-ignore
            if(!webSocketURL?.length) {
                return;
            }
            let pingTimer: ReturnType<typeof setInterval> | null = null;
            const socket = new WebSocket(webSocketURL);

            const getCseg = () => {
                //@ts-ignore
                return socket.cseq ?? 1;
            }

            const increaseCseq = () => {
                const cseq = getCseg() + 1;
                //@ts-ignore
                socket.cseq = cseq;
            }

            const setCseg = (cseq: number) => {
                //@ts-ignore
                socket.cseq = cseq;
            }

            socket.onopen = function() {
                socket.send(JSON.stringify({
                    "cseq": getCseg(),
                    "auth_info": {
                        "session_id": session_id,
                        "csrf_token": csrf_token
                    },
                    "service": "CallControl",
                    "method": "enable_api_notifications",
                    "params": {
                        "event": "sip.call_control_notifications",
                        "i_customer": i_customer
                    }
                }));
                
                //@ts-ignore
                pingTimer = setInterval(() => {
                    increaseCseq();
                    socket.send(JSON.stringify({
                        "cseq": getCseg(),
                        "service": "Session",
                        "method": "ping",
                        "params": {
                            "session_id": session_id,
                            "csrf_token": csrf_token
                        }
                    }));
                }, 2 * 60 * 1000); 
            };
            
            socket.onmessage = (event) => {
                const object: WebSocketObject = JSON.parse(event.data);
                if(object.cseq) {
                    setCseg(object.cseq);
                } else {
                    increaseCseq();
                }
                updateStatus(object);
            };

            socket.onclose = function() {
                //@ts-ignore
                if(pingTimer) {
                    //@ts-ignore
                    clearInterval(pingTimer);
                }
            };
            
            socket.onerror = function() {
                //@ts-ignore
                if(pingTimer) {
                    //@ts-ignore
                    clearInterval(pingTimer);
                }
            };

            //@ts-ignore
            window.signaling = socket;
            
            return () => {
                if (pingTimer) clearInterval(pingTimer);
                socket.close();
            };
        }
    }, [webSocketURL, session_id, csrf_token, dispatch, i_customer]);

    useEffect(() => {
        if(!webSocketURL?.length) {
            return;
        }
        const interval = setInterval(() => {
            recalcCallsDurationEvent?.();
        }, 1000);
        return () => clearInterval(interval);
    }, [webSocketURL, recalcCallsDurationEvent]);

    useEffect(() => {
        const unlisten = history.listen((location) => {
            if (location.pathname !== prevLocation) {
                //@ts-ignore
                window.signaling?.close();
                //@ts-ignore
                window.signaling = null;
            }
        });
        return () => {
            unlisten();
        }
    }, [history]);

    return (
        <>
            {children}
        </>
    );
};

export default WebSocketDataProvider;
